import { useState } from 'react';
import { apiErrorText, useUploadAccumulationPackMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import { Err, FileHint, SubmitWrapperSm, TableTitle } from './styled';

interface MonthlyPackUploadFormProps {
  ledgerId: string;
  canWrite: boolean;
}

export function MonthlyPackUploadForm({ ledgerId, canWrite }: MonthlyPackUploadFormProps) {
  const [periodMonth, setPeriodMonth] = useState('');
  const [docDate, setDocDate] = useState('');
  const [ks3No, setKs3No] = useState('');
  const [paFiles, setPaFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [uploadPack, { isLoading: uploading }] = useUploadAccumulationPackMutation();

  async function handleUploadPack(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!ledgerId) return;
    if (paFiles.length === 0) {
      setError('Приложите хотя бы один первичный акт.');
      return;
    }
    const fd = new FormData();
    if (periodMonth.trim()) fd.append('period_month', periodMonth);
    if (docDate.trim()) fd.append('doc_date', docDate);
    if (ks3No.trim()) fd.append('ks3_no', ks3No);
    paFiles.forEach((f) => fd.append('file', f));
    try {
      await uploadPack({ id: ledgerId, form: fd }).unwrap();
      setPaFiles([]);
      setPeriodMonth('');
      setDocDate('');
      setKs3No('');
    } catch (err) {
      setError(
        apiErrorText(err, 'Не удалось запустить дозаполнение. Проверьте формат первичных актов.'),
      );
      // eslint-disable-next-line no-console
      console.error('uploadAccumulationPack failed', err);
    }
  }

  return (
    <>
      <TableTitle>Дозаполнить за новый месяц (пачка первичных актов)</TableTitle>
      <form onSubmit={handleUploadPack}>
        <Field>
          <Label>Месяц (опционально — иначе из каждого ПА)</Label>
          <Input
            type="month"
            value={periodMonth}
            onChange={(e) => setPeriodMonth(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Дата документа (опционально — иначе конец месяца ПА)</Label>
          <Input
            type="date"
            value={docDate}
            onChange={(e) => setDocDate(e.target.value)}
          />
          <FileHint>
            Печатается в графе «Дата» книги. В самом первичном акте даты нет — только
            отчётный месяц.
          </FileHint>
        </Field>
        <Field>
          <Label>№ КС-3 (опционально — если справка уже выпущена)</Label>
          <Input
            placeholder="Например: 24"
            value={ks3No}
            onChange={(e) => setKs3No(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Первичные акты (Excel)</Label>
          <Input
            type="file"
            multiple
            accept=".xlsx,.xlsm"
            onChange={(e) => setPaFiles(Array.from(e.target.files ?? []))}
          />
          <FileHint>Выбрано файлов: {paFiles.length}</FileHint>
        </Field>
        {error && <Err>{error}</Err>}
        <SubmitWrapperSm>
          <Button
            type="submit"
            $variant="primary"
            $size="sm"
            disabled={uploading || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {uploading ? 'Запуск…' : 'Дозаполнить ленту'}
          </Button>
        </SubmitWrapperSm>
      </form>
    </>
  );
}
